/**
 * Shipping Calculator — Concept 6.0.2
 *
 * Calcula o frete na página de produto usando SOMENTE as APIs nativas da Shopify:
 *   POST {cart_url}/prepare_shipping_rates.json
 *   GET  {cart_url}/async_shipping_rates.json
 *   GET  {cart_url}/shipping_rates.json   (fallback)
 *
 * A lógica fica em uma classe comum (ShippingCalculatorController), NÃO no custom
 * element. O <shipping-calculator> apenas dispara o mount. Assim, se o registro de
 * custom elements falhar por qualquer motivo, um scan no DOMContentLoaded
 * inicializa o bloco do mesmo jeito.
 */

(function () {
  'use strict';

  /* Faixas de CEP por UF (fallback offline, sem depender de API externa). */
  var CEP_RANGES = [
    [1000000, 19999999, 'SP'],
    [20000000, 28999999, 'RJ'],
    [29000000, 29999999, 'ES'],
    [30000000, 39999999, 'MG'],
    [40000000, 48999999, 'BA'],
    [49000000, 49999999, 'SE'],
    [50000000, 56999999, 'PE'],
    [57000000, 57999999, 'AL'],
    [58000000, 58999999, 'PB'],
    [59000000, 59999999, 'RN'],
    [60000000, 63999999, 'CE'],
    [64000000, 64999999, 'PI'],
    [65000000, 65999999, 'MA'],
    [66000000, 68899999, 'PA'],
    [68900000, 68999999, 'AP'],
    [69000000, 69299999, 'AM'],
    [69300000, 69399999, 'RR'],
    [69400000, 69899999, 'AM'],
    [69900000, 69999999, 'AC'],
    [70000000, 72799999, 'DF'],
    [72800000, 72999999, 'GO'],
    [73000000, 73699999, 'DF'],
    [73700000, 76799999, 'GO'],
    [76800000, 76999999, 'RO'],
    [77000000, 77999999, 'TO'],
    [78000000, 78899999, 'MT'],
    [78900000, 78999999, 'RO'],
    [79000000, 79999999, 'MS'],
    [80000000, 87999999, 'PR'],
    [88000000, 89999999, 'SC'],
    [90000000, 99999999, 'RS']
  ];

  var UF_NAMES = {
    AC: 'Acre',
    AL: 'Alagoas',
    AP: 'Amapá',
    AM: 'Amazonas',
    BA: 'Bahia',
    CE: 'Ceará',
    DF: 'Distrito Federal',
    ES: 'Espírito Santo',
    GO: 'Goiás',
    MA: 'Maranhão',
    MT: 'Mato Grosso',
    MS: 'Mato Grosso do Sul',
    MG: 'Minas Gerais',
    PA: 'Pará',
    PB: 'Paraíba',
    PR: 'Paraná',
    PE: 'Pernambuco',
    PI: 'Piauí',
    RJ: 'Rio de Janeiro',
    RN: 'Rio Grande do Norte',
    RS: 'Rio Grande do Sul',
    RO: 'Rondônia',
    RR: 'Roraima',
    SC: 'Santa Catarina',
    SP: 'São Paulo',
    SE: 'Sergipe',
    TO: 'Tocantins'
  };

  var STORAGE_KEY = 'theme:shipping-calculator:zip';

  function ShippingCalculatorController(root) {
    this.root = root;
    this.input = root.querySelector('[data-shipping-zip]');
    this.button = root.querySelector('[data-shipping-submit]');
    this.results = root.querySelector('[data-shipping-results]');

    if (!this.input || !this.results) {
      console.warn('[shipping-calculator] markup incompleto: campo de CEP ou área de resultado não encontrados.', root);
      return;
    }

    var data = root.dataset;
    this.cartUrl = (data.cartUrl || '/cart').replace(/\/$/, '');
    this.country = data.country || 'Brazil';
    this.useViaCep = data.viacep === 'true';
    this.rememberZip = data.remember === 'true';
    this.strings = this.safeJSON(data.strings) || {};
    this.pendingRestore = null;

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.onPageHide = this.onPageHide.bind(this);

    /* Delegação no próprio bloco: funciona mesmo que o markup interno mude. */
    root.addEventListener('click', this.onClick);
    root.addEventListener('keydown', this.onKeydown);
    root.addEventListener('input', this.onInput);
    root.addEventListener('submit', this.onSubmit);
    window.addEventListener('pagehide', this.onPageHide);
    document.addEventListener('variant:change', this.onVariantChange);

    /* Delegado no document: o tema substitui o input de quantidade ao trocar
       de variante, então uma referência direta ficaria órfã. */
    document.addEventListener('change', this.onQuantityChange);
    document.addEventListener('input', this.onQuantityChange);

    try {
      var pubsub = window.theme && window.theme.pubsub;
      if (pubsub && pubsub.subscribe && pubsub.PUB_SUB_EVENTS && pubsub.PUB_SUB_EVENTS.variantChange) {
        var self = this;
        this.unsubscribeVariant = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, function (event) {
          var payload = event && event.data;
          if (payload && payload.sectionId && payload.sectionId !== data.sectionId) return;
          self.onVariantChange({ detail: { variant: payload && payload.variant } });
        });
      }
    } catch (error) {
      console.warn('[shipping-calculator] pubsub indisponível', error);
    }

    if (this.rememberZip) {
      var saved = this.readStoredZip();
      if (saved) {
        this.input.value = this.maskZip(saved);
        if (data.autoCalculate === 'true') this.calculate();
      }
    }

    this.ready = true;
  }

  ShippingCalculatorController.prototype = {
    constructor: ShippingCalculatorController,

    destroy: function () {
      if (!this.ready) return;
      this.root.removeEventListener('click', this.onClick);
      this.root.removeEventListener('keydown', this.onKeydown);
      this.root.removeEventListener('input', this.onInput);
      this.root.removeEventListener('submit', this.onSubmit);
      window.removeEventListener('pagehide', this.onPageHide);
      document.removeEventListener('variant:change', this.onVariantChange);
      document.removeEventListener('change', this.onQuantityChange);
      document.removeEventListener('input', this.onQuantityChange);
      clearTimeout(this.quantityTimer);
      if (this.unsubscribeVariant) this.unsubscribeVariant();
      this.restoreCart();
    },

    /* ------------------------------------------------------------------ */
    /* Eventos                                                             */
    /* ------------------------------------------------------------------ */

    onClick: function (event) {
      if (!event.target.closest('[data-shipping-submit]')) return;
      event.preventDefault();
      event.stopPropagation();
      this.calculate();
    },

    onKeydown: function (event) {
      if (event.key !== 'Enter') return;
      if (!event.target.matches('[data-shipping-zip]')) return;
      event.preventDefault();
      event.stopPropagation();
      this.calculate();
    },

    onInput: function (event) {
      if (!event.target.matches('[data-shipping-zip]')) return;
      var masked = this.maskZip(this.input.value);
      if (masked !== this.input.value) this.input.value = masked;
      this.input.classList.remove('invalid');
    },

    /* Rede de segurança: se algum <form> ancestral tentar submeter, cancela. */
    onSubmit: function (event) {
      event.preventDefault();
      event.stopPropagation();
      this.calculate();
    },

    onVariantChange: function (event) {
      var variant = event && event.detail && event.detail.variant;
      if (variant && variant.id) this.root.dataset.variantId = variant.id;
      this.clearResults();
    },

    /**
     * Recalcula quando a quantidade muda, para o valor na tela nunca ficar
     * desatualizado. Só age se já houver um resultado visível.
     */
    onQuantityChange: function (event) {
      var target = event.target;
      if (!target || target.name !== 'quantity') return;

      var expectedId = 'Quantity-' + this.root.dataset.sectionId + '-' + this.root.dataset.productId;
      if (target.id && target.id !== expectedId) return;

      var quantity = parseInt(target.value, 10);
      if (!isFinite(quantity) || quantity < 1) return;
      if (quantity === this.lastQuantity) return;

      if (!this.hasResult) return;

      var self = this;
      clearTimeout(this.quantityTimer);
      this.quantityTimer = setTimeout(function () {
        if (self.root.dataset.loading === 'true') return;
        self.calculate();
      }, 600);
    },

    clearResults: function () {
      this.results.innerHTML = '';
      this.hasResult = false;
    },

    onPageHide: function () {
      /* Garante que o item temporário não fique no carrinho se a aba fechar. */
      if (!this.pendingRestore || !navigator.sendBeacon) return;
      var body = new URLSearchParams();
      body.set('id', this.pendingRestore.key);
      body.set('quantity', String(this.pendingRestore.quantity));
      navigator.sendBeacon(this.cartUrl + '/change.js', body);
      this.pendingRestore = null;
    },

    /* ------------------------------------------------------------------ */
    /* Fluxo principal                                                     */
    /* ------------------------------------------------------------------ */

    calculate: function () {
      var self = this;
      var zip = this.digits(this.input.value);

      if (zip.length !== 8) {
        this.input.classList.add('invalid');
        this.renderMessage(this.t('invalid_zip'), 'error');
        this.input.focus();
        return;
      }

      if (this.rememberZip) this.storeZip(zip);

      this.lastQuantity = this.getQuantity();
      this.setLoading(true);
      this.renderSkeleton();

      return this.resolveLocation(zip)
        .then(function (location) {
          if (!location.province) {
            self.renderMessage(self.t('invalid_zip'), 'error');
            return;
          }
          return self.fetchRates(zip, location).then(function (rates) {
            if (!rates || rates.length === 0) {
              self.renderMessage(self.t('no_rates'), 'info');
              return;
            }
            self.renderRates(rates, location);
          });
        })
        .catch(function (error) {
          console.error('[shipping-calculator]', error);
          self.renderMessage(self.t('error'), 'error');
        })
        .then(function () {
          self.setLoading(false);
        });
    },

    /**
     * Adiciona o produto ao carrinho (se necessário), consulta as taxas nativas
     * e devolve o carrinho ao estado original.
     */
    fetchRates: function (zip, location) {
      var self = this;
      return this.ensureItemInCart().then(function (restore) {
        return self
          .requestShippingRates(zip, location)
          .then(function (rates) {
            return self.restoreCart(restore).then(function () {
              return rates;
            });
          })
          .catch(function (error) {
            return self.restoreCart(restore).then(function () {
              throw error;
            });
          });
      });
    },

    ensureItemInCart: function () {
      var self = this;
      var variantId = parseInt(this.root.dataset.variantId, 10);
      if (!variantId) return Promise.resolve(null);

      var quantity = this.lastQuantity || this.getQuantity();

      return this.getJSON(this.cartUrl + '.js')
        .then(function (cart) {
          var existing = (cart.items || []).filter(function (item) {
            return item.variant_id === variantId;
          })[0];
          var currentQuantity = existing ? existing.quantity : 0;

          if (currentQuantity >= quantity) return null;

          return self
            .postJSON(self.cartUrl + '/add.js', {
              items: [{ id: variantId, quantity: quantity - currentQuantity }]
            })
            .then(function () {
              return self.getJSON(self.cartUrl + '.js');
            })
            .then(function (updatedCart) {
              var line = (updatedCart.items || []).filter(function (item) {
                return item.variant_id === variantId;
              })[0];
              if (!line) return null;
              var restore = { key: line.key, quantity: currentQuantity };
              self.pendingRestore = restore;
              return restore;
            });
        })
        .catch(function (error) {
          console.warn('[shipping-calculator] não foi possível preparar o carrinho', error);
          return null;
        });
    },

    restoreCart: function (restore) {
      restore = restore || this.pendingRestore;
      if (!restore) return Promise.resolve();
      this.pendingRestore = null;
      return this.postJSON(this.cartUrl + '/change.js', {
        id: restore.key,
        quantity: restore.quantity
      }).catch(function (error) {
        console.error('[shipping-calculator] falha ao restaurar o carrinho', error);
      });
    },

    /**
     * prepare_shipping_rates.json + polling em async_shipping_rates.json,
     * que é o método recomendado pela Shopify. Cai para shipping_rates.json
     * se o fluxo assíncrono não responder.
     */
    requestShippingRates: function (zip, location) {
      var self = this;
      var params = new URLSearchParams();
      params.set('shipping_address[zip]', this.maskZip(zip));
      params.set('shipping_address[country]', this.country);
      params.set('shipping_address[province]', location.province);
      if (location.city) params.set('shipping_address[city]', location.city);

      var query = params.toString();

      function poll(attempt) {
        if (attempt >= 20) return Promise.resolve(null);
        return fetch(self.cartUrl + '/async_shipping_rates.json?' + query, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (payload) {
            if (payload && Array.isArray(payload.shipping_rates)) return payload.shipping_rates;
            return self.wait(500).then(function () {
              return poll(attempt + 1);
            });
          });
      }

      return fetch(this.cartUrl + '/prepare_shipping_rates.json?' + query, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function () {
          return poll(0);
        })
        .catch(function (error) {
          console.warn('[shipping-calculator] fluxo assíncrono indisponível, usando fallback', error);
          return null;
        })
        .then(function (rates) {
          if (rates) return rates;
          return self.getJSON(self.cartUrl + '/shipping_rates.json?' + query).then(function (payload) {
            return (payload && payload.shipping_rates) || [];
          });
        });
    },

    /* ------------------------------------------------------------------ */
    /* Localização                                                         */
    /* ------------------------------------------------------------------ */

    resolveLocation: function (zip) {
      var self = this;

      function local() {
        var uf = self.ufFromZip(zip);
        return {
          province: uf ? UF_NAMES[uf] : '',
          uf: uf,
          city: '',
          street: '',
          neighborhood: ''
        };
      }

      if (!this.useViaCep) return Promise.resolve(local());

      var controller = new AbortController();
      var timeout = setTimeout(function () {
        controller.abort();
      }, 3500);

      return fetch('https://viacep.com.br/ws/' + zip + '/json/', { signal: controller.signal })
        .then(function (response) {
          clearTimeout(timeout);
          return response.json();
        })
        .then(function (data) {
          if (!data || data.erro || !data.uf) return local();
          return {
            province: UF_NAMES[data.uf] || data.uf,
            uf: data.uf,
            city: data.localidade || '',
            street: data.logradouro || '',
            neighborhood: data.bairro || ''
          };
        })
        .catch(function () {
          clearTimeout(timeout);
          return local();
        });
    },

    ufFromZip: function (zip) {
      var value = parseInt(zip, 10);
      for (var i = 0; i < CEP_RANGES.length; i += 1) {
        if (value >= CEP_RANGES[i][0] && value <= CEP_RANGES[i][1]) return CEP_RANGES[i][2];
      }
      return '';
    },

    /* ------------------------------------------------------------------ */
    /* Render                                                              */
    /* ------------------------------------------------------------------ */

    renderSkeleton: function () {
      this.results.innerHTML =
        '<div class="shipping-calculator__skeleton" aria-hidden="true"><span></span><span></span><span></span></div>';
    },

    renderMessage: function (message, status) {
      this.hasResult = false;
      this.results.innerHTML =
        '<p class="shipping-calculator__message text-sm" data-status="' +
        status +
        '">' +
        this.escape(message) +
        '</p>';
    },

    renderRates: function (rates, location) {
      var self = this;
      this.hasResult = true;
      var destination = [location.street, location.neighborhood, location.city].filter(Boolean).join(', ');
      var label = [destination, location.uf].filter(Boolean).join(' - ');

      var items = rates
        .map(function (rate) {
          var cents = self.toCents(rate.price);
          var isFree = cents === 0;
          var price = isFree ? self.t('free') : self.formatMoney(cents);
          var eta = self.deliveryEstimate(rate);

          return (
            '<li class="shipping-calculator__rate">' +
            '<span class="shipping-calculator__rate-info">' +
            '<span class="shipping-calculator__rate-name text-sm">' +
            self.escape(rate.presentment_name || rate.name || rate.code || '') +
            '</span>' +
            (eta ? '<span class="shipping-calculator__rate-eta text-xs">' + self.escape(eta) + '</span>' : '') +
            '</span>' +
            '<span class="shipping-calculator__rate-price text-sm"' +
            (isFree ? ' data-free' : '') +
            '>' +
            price +
            '</span>' +
            '</li>'
          );
        })
        .join('');

      this.results.innerHTML =
        (label
          ? '<p class="shipping-calculator__destination text-xs">' +
            this.escape(this.t('destination') + ' ' + label) +
            '</p>'
          : '') +
        '<ul class="shipping-calculator__list">' +
        items +
        '</ul>';
    },

    deliveryEstimate: function (rate) {
      if (rate.delivery_days && rate.delivery_days.length) {
        var days = rate.delivery_days;
        var min = days[0];
        var max = days[days.length - 1];
        var range = min === max ? String(min) : min + '–' + max;
        return this.t('eta_prefix') + ' ' + range + ' ' + this.t('business_days');
      }

      if (rate.delivery_range && rate.delivery_range.length) {
        var self = this;
        var dates = rate.delivery_range
          .map(function (value) {
            return self.formatDate(value);
          })
          .filter(Boolean);
        if (dates.length === 2 && dates[0] !== dates[1]) {
          return this.t('eta_between') + ' ' + dates[0] + ' ' + this.t('eta_and') + ' ' + dates[1];
        }
        if (dates.length) return this.t('eta_prefix_date') + ' ' + dates[0];
      }

      if (rate.estimated_time_in_transit) {
        return this.t('eta_prefix') + ' ' + rate.estimated_time_in_transit + ' ' + this.t('business_days');
      }

      return '';
    },

    setLoading: function (state) {
      this.root.dataset.loading = String(state);
      if (!this.button) return;
      this.button.disabled = state;
      this.button.setAttribute('aria-busy', String(state));
      var text = this.button.querySelector('.btn-text') || this.button;
      if (state) {
        this.buttonLabel = text.textContent;
        text.textContent = this.t('loading');
      } else if (this.buttonLabel) {
        text.textContent = this.buttonLabel;
      }
    },

    /* ------------------------------------------------------------------ */
    /* Utilidades                                                          */
    /* ------------------------------------------------------------------ */

    getQuantity: function () {
      var data = this.root.dataset;
      var form = data.productFormId ? document.getElementById(data.productFormId) : null;
      var input =
        document.getElementById('Quantity-' + data.sectionId + '-' + data.productId) ||
        (form && form.querySelector('input[name="quantity"]')) ||
        (data.productFormId && document.querySelector('input[name="quantity"][form="' + data.productFormId + '"]'));

      var value = input ? parseInt(input.value, 10) : NaN;
      return isFinite(value) && value > 0 ? value : 1;
    },

    toCents: function (price) {
      if (typeof price === 'number') return Math.round(price);
      var value = String(price == null ? '0' : price);
      return value.indexOf('.') !== -1 ? Math.round(parseFloat(value) * 100) : parseInt(value, 10) || 0;
    },

    formatMoney: function (cents) {
      if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
        return window.theme.Currency.formatMoney(cents, window.theme.settings && window.theme.settings.moneyFormat);
      }
      return (cents / 100).toLocaleString(document.documentElement.lang || 'pt-BR', {
        style: 'currency',
        currency: this.root.dataset.currency || 'BRL'
      });
    },

    formatDate: function (value) {
      var date = new Date(value);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString(document.documentElement.lang || 'pt-BR', {
        day: '2-digit',
        month: '2-digit'
      });
    },

    digits: function (value) {
      return String(value || '')
        .replace(/\D/g, '')
        .slice(0, 8);
    },

    maskZip: function (value) {
      var digits = this.digits(value);
      return digits.length > 5 ? digits.slice(0, 5) + '-' + digits.slice(5) : digits;
    },

    readStoredZip: function () {
      try {
        return localStorage.getItem(STORAGE_KEY) || '';
      } catch (error) {
        return '';
      }
    },

    storeZip: function (zip) {
      try {
        localStorage.setItem(STORAGE_KEY, zip);
      } catch (error) {
        /* Ignora navegadores/iframes com storage bloqueado. */
      }
    },

    t: function (key) {
      return this.strings[key] || '';
    },

    escape: function (value) {
      var element = document.createElement('div');
      element.textContent = String(value == null ? '' : value);
      return element.innerHTML;
    },

    safeJSON: function (value) {
      try {
        return JSON.parse(value);
      } catch (error) {
        console.warn('[shipping-calculator] não foi possível ler data-strings', error);
        return null;
      }
    },

    wait: function (ms) {
      return new Promise(function (resolve) {
        setTimeout(resolve, ms);
      });
    },

    getJSON: function (url) {
      return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(function (response) {
        return response.json();
      });
    },

    postJSON: function (url, body) {
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      }).then(function (response) {
        return response.json();
      });
    }
  };

  /* -------------------------------------------------------------------- */
  /* Boot                                                                  */
  /* -------------------------------------------------------------------- */

  function mount(root) {
    if (!root || root.shippingCalculator) return;
    try {
      root.shippingCalculator = new ShippingCalculatorController(root);
    } catch (error) {
      console.error('[shipping-calculator] falha ao iniciar o bloco', error);
    }
  }

  function boot() {
    var roots = document.querySelectorAll('shipping-calculator');
    for (var i = 0; i < roots.length; i += 1) mount(roots[i]);
  }

  if (!customElements.get('shipping-calculator')) {
    try {
      customElements.define(
        'shipping-calculator',
        class extends HTMLElement {
          connectedCallback() {
            mount(this);
          }
          disconnectedCallback() {
            if (this.shippingCalculator) {
              this.shippingCalculator.destroy();
              this.shippingCalculator = null;
            }
          }
        }
      );
    } catch (error) {
      console.warn('[shipping-calculator] custom element não registrado, usando fallback', error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Reinicializa quando o editor de temas recarrega a seção. */
  document.addEventListener('shopify:section:load', boot);

  window.ShippingCalculatorReady = true;
  console.info('[shipping-calculator] script carregado');
})();
